import type {
  BootstrapData,
  Cliente,
  DashboardSummary,
  Factura,
  Pedido,
  Producto,
  Tasa,
  Usuario,
} from '../api/types';

const NOW = new Date();

function iso(daysAgo: number, hour = 10, minute = 30): string {
  const date = new Date(NOW);
  date.setDate(date.getDate() - daysAgo);
  date.setHours(hour, minute, 0, 0);
  return date.toISOString();
}

function ymd(daysAgo: number): string {
  return iso(daysAgo).slice(0, 10);
}

export const demoUser: Usuario = {
  id: '33333333-3333-4333-8333-333333333301',
  empresaId: '11111111-1111-4111-8111-111111111111',
  empresaNombre: 'GRAVENCA, C.A.',
  empresaRif: 'J-40000000-1',
  nombre: 'Luis Marcano',
  email: 'demo@gravenca.local',
  rol: 'admin',
  vendedorCodigo: 'V-00',
};

export const demoRates: Tasa[] = [
  {
    moneda: 'USD',
    tasa: 36.42,
    fuente: 'BCV',
    vigenteDesde: iso(0, 8, 0),
  },
  {
    moneda: 'EUR',
    tasa: 39.87,
    fuente: 'BCV',
    vigenteDesde: iso(0, 8, 0),
  },
];

export const demoProducts: Producto[] = [
  {
    id: '55555555-5555-4555-8555-555555555501',
    sku: 'FERT-151515',
    nombre: 'FERTI NITRO 15-15-15 (saco 50 kg)',
    categoria: 'fertilizantes',
    unidad: 'SAO',
    precioUsd: 28.5,
    impuestoPct: 0,
    stockActual: 640,
    stockRuta: 128,
    activo: true,
  },
  {
    id: '55555555-5555-4555-8555-555555555502',
    sku: 'UREA-46',
    nombre: 'Urea 46% (saco 50 kg)',
    categoria: 'fertilizantes',
    unidad: 'SAO',
    precioUsd: 24.9,
    impuestoPct: 0,
    stockActual: 520,
    stockRuta: 104,
    activo: true,
  },
  {
    id: '55555555-5555-4555-8555-555555555503',
    sku: 'DAP-18460',
    nombre: 'Fosfato Diamónico 18-46-0 (saco 50 kg)',
    categoria: 'fertilizantes',
    unidad: 'SAO',
    precioUsd: 32.75,
    impuestoPct: 0,
    stockActual: 380,
    stockRuta: 72,
    activo: true,
  },
  {
    id: '55555555-5555-4555-8555-555555555504',
    sku: 'KCL-60',
    nombre: 'Cloruro de Potasio 60% (saco 50 kg)',
    categoria: 'fertilizantes',
    unidad: 'SAO',
    precioUsd: 21.4,
    impuestoPct: 0,
    stockActual: 410,
    stockRuta: 96,
    activo: true,
  },
  {
    id: '55555555-5555-4555-8555-555555555505',
    sku: 'CAL-AGRO',
    nombre: 'Cal Agrícola (saco 25 kg)',
    categoria: 'enmiendas',
    unidad: 'SAO',
    precioUsd: 6.8,
    impuestoPct: 0,
    stockActual: 800,
    stockRuta: 190,
    activo: true,
  },
  {
    id: '55555555-5555-4555-8555-555555555506',
    sku: 'BIO-RAD-1L',
    nombre: 'Bioestimulante Radicular 1 L',
    categoria: 'bioinsumos',
    unidad: 'L',
    precioUsd: 14.25,
    impuestoPct: 16,
    stockActual: 240,
    stockRuta: 48,
    activo: true,
  },
  {
    id: '55555555-5555-4555-8555-555555555507',
    sku: 'HERB-GLI-1L',
    nombre: 'Herbicida Glifosato 1 L',
    categoria: 'protección',
    unidad: 'L',
    precioUsd: 9.9,
    impuestoPct: 16,
    stockActual: 300,
    stockRuta: 64,
    activo: true,
  },
  {
    id: '55555555-5555-4555-8555-555555555508',
    sku: 'FUNG-SIS-500',
    nombre: 'Fungicida Sistémico 500 ml',
    categoria: 'protección',
    unidad: 'FRA',
    precioUsd: 18.6,
    impuestoPct: 16,
    stockActual: 210,
    stockRuta: 40,
    activo: true,
  },
  {
    id: '55555555-5555-4555-8555-555555555509',
    sku: 'SEM-MAIZ-20',
    nombre: 'Semilla Maíz Híbrido (bolsa 20 kg)',
    categoria: 'semillas',
    unidad: 'BOL',
    precioUsd: 78,
    impuestoPct: 0,
    stockActual: 160,
    stockRuta: 28,
    activo: true,
  },
  {
    id: '55555555-5555-4555-8555-555555555510',
    sku: 'SACO-YUTE',
    nombre: 'Saco de Yute 50 kg',
    categoria: 'empaque',
    unidad: 'UND',
    precioUsd: 3.2,
    impuestoPct: 16,
    stockActual: 1200,
    stockRuta: 240,
    activo: true,
  },
];

const CLIENT_ROWS: Array<[string, string, string, string]> = [
  ['44444444-4444-4444-8444-444444444401', 'J-40123456-0', 'Agropecuaria Los Llanos, C.A.', 'María Pérez'],
  ['44444444-4444-4444-8444-444444444402', 'J-40234567-1', 'Hacienda La Esperanza', 'María Pérez'],
  ['44444444-4444-4444-8444-444444444403', 'J-40345678-2', 'Distribuidora El Trigal', 'José Rodríguez'],
  ['44444444-4444-4444-8444-444444444404', 'J-40456789-3', 'Finca Santa Rosa', 'José Rodríguez'],
  ['44444444-4444-4444-8444-444444444405', 'J-40567890-4', 'Agrosur del Táchira', 'María Pérez'],
  ['44444444-4444-4444-8444-444444444406', 'J-40678901-5', 'Cooperativa Campo Verde', 'José Rodríguez'],
  ['44444444-4444-4444-8444-444444444407', 'J-40789012-6', 'Inversiones La Ceiba', 'María Pérez'],
  ['44444444-4444-4444-8444-444444444408', 'J-40890123-7', 'Agroindustrias del Centro', 'José Rodríguez'],
  ['44444444-4444-4444-8444-444444444409', 'J-40901234-8', 'Fundo Los Robles', 'María Pérez'],
  ['44444444-4444-4444-8444-444444444410', 'J-41012345-9', 'Suministros Agrícolas Andinos', 'José Rodríguez'],
  ['44444444-4444-4444-8444-444444444411', 'J-41123456-0', 'Hacienda El Milagro', 'María Pérez'],
  ['44444444-4444-4444-8444-444444444412', 'J-41234567-1', 'Comercial Agro-Zulia', 'José Rodríguez'],
];

export const demoClients: Cliente[] = CLIENT_ROWS.map(([id, rif, nombre, vendedor], index) => ({
  id,
  rif,
  nombre,
  razonSocial: nombre,
  direccion: 'Sector agroindustrial, Venezuela',
  telefono: `+58 412-${String(4100000 + index * 137).slice(-7)}`,
  email: null,
  limiteCredito: 25000,
  diasCredito: 30,
  activo: true,
  vendedorId:
    vendedor === 'María Pérez'
      ? '33333333-3333-4333-8333-333333333302'
      : '33333333-3333-4333-8333-333333333303',
  vendedorNombre: vendedor,
}));

function pedido(
  index: number,
  daysAgo: number,
  clienteIndex: number,
  estado: Pedido['estado'],
  totalUsd: number,
  cajas: number,
  origen: Pedido['origen'] = 'web',
): Pedido {
  const cliente = demoClients[clienteIndex % demoClients.length] ?? demoClients[0]!;
  return {
    id: `aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaa${String(index).padStart(4, '0')}`,
    numero: `PED-${ymd(daysAgo).replaceAll('-', '')}-${String(index).padStart(5, '0')}`,
    estado,
    moneda: 'USD',
    tasaAplicada: 36.42,
    totalUsd,
    totalMoneda: Math.round(totalUsd * 36.42 * 100) / 100,
    createdAt: iso(daysAgo, 9 + (index % 7), 15 + (index % 40)),
    origen,
    clienteNombre: cliente.nombre,
    clienteRif: cliente.rif,
    vendedorNombre: cliente.vendedorNombre ?? 'María Pérez',
    cajas,
  };
}

export const demoOrders: Pedido[] = [
  pedido(22, 0, 7, 'en_cola', 247.95, 15, 'offline'),
  pedido(21, 0, 0, 'procesada', 1936.5, 60),
  pedido(20, 0, 3, 'enviada', 1104.2, 36),
  pedido(19, 1, 5, 'procesada', 2814.75, 88),
  pedido(18, 1, 9, 'rechazada', 642.4, 22),
  pedido(17, 2, 2, 'procesada', 3240.1, 105),
  pedido(16, 3, 4, 'enviada', 1580.6, 49),
  pedido(15, 4, 8, 'procesada', 2210.9, 71),
];

export const demoInvoices: Factura[] = demoOrders
  .filter((order) => order.estado === 'procesada')
  .map((order, index) => {
    const totalUsd = order.totalUsd;
    const subtotalUsd = Math.round((totalUsd / 1.16) * 100) / 100;
    const impuestoUsd = Math.round((totalUsd - subtotalUsd) * 100) / 100;
    return {
      id: `bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbb${String(index + 1).padStart(4, '0')}`,
      numero: `FAC-${String(1 + index).padStart(5, '0')}`,
      numeroControl: `00-${String(501 + index).padStart(5, '0')}`,
      estado: 'emitida' as const,
      tipo: 'ARTICULO' as const,
      exenta: false,
      moneda: 'USD' as const,
      tasa: 36.42,
      subtotalUsd,
      impuestoUsd,
      totalUsd,
      subtotalVes: Math.round(subtotalUsd * 36.42 * 100) / 100,
      impuestoVes: Math.round(impuestoUsd * 36.42 * 100) / 100,
      totalVes: Math.round(totalUsd * 36.42 * 100) / 100,
      saldoUsd: totalUsd,
      saldoVes: Math.round(totalUsd * 36.42 * 100) / 100,
      fechaEmision: order.createdAt.slice(0, 10),
      fechaVencimiento: order.createdAt.slice(0, 10),
      createdAt: order.createdAt,
      clienteNombre: order.clienteNombre,
      clienteRif: order.clienteRif,
      vendedorNombre: order.vendedorNombre,
      proyectoNombre: null,
    };
  });

export const demoBootstrap: BootstrapData = {
  serverTime: new Date().toISOString(),
  usuario: {
    id: demoUser.id,
    nombre: demoUser.nombre,
    rol: demoUser.rol,
    vendedorCodigo: demoUser.vendedorCodigo,
  },
  empresa: {
    id: demoUser.empresaId,
    nombre: demoUser.empresaNombre,
    rif: demoUser.empresaRif,
  },
  sucursales: [
    {
      id: '22222222-2222-4222-8222-222222222201',
      codigo: 'PRINCIPAL',
      nombre: 'Sede Principal',
    },
    {
      id: '22222222-2222-4222-8222-222222222202',
      codigo: 'CENTRO',
      nombre: 'Centro de Distribución Centro',
    },
  ],
  productos: demoProducts,
  clientes: demoClients,
  tasas: demoRates,
  meta: {
    metaUsd: 45000,
    metaCajas: 3500,
    periodo: ymd(0).slice(0, 7) + '-01',
  },
  demo: true,
};

export const demoDashboard: DashboardSummary = {
  fecha: ymd(0),
  kpis: {
    pedidosHoy: 3,
    cajasHoy: 140,
    cobranzaHoyUsd: 2293.02,
    cobranzaHoyVes: 2293.02 * 36.42,
    diasCallePromedio: 7,
    clientesActivos: 12,
    clientesComprando: 12,
    ventasMesUsd: 39442.75,
    ventasMesVes: 39442.75 * 36.42,
    metaUsd: 45000,
    metaCajas: 3500,
    cumplimientoMeta: 88,
  },
  serie: [
    { fecha: ymd(6), cajas: 322, pedidos: 3, totalUsd: 6034.46, totalVes: 6034.46 * 36.42 },
    { fecha: ymd(5), cajas: 335, pedidos: 3, totalUsd: 7238.23, totalVes: 7238.23 * 36.42 },
    { fecha: ymd(4), cajas: 293, pedidos: 3, totalUsd: 8367.3, totalVes: 8367.3 * 36.42 },
    { fecha: ymd(3), cajas: 266, pedidos: 3, totalUsd: 7184.92, totalVes: 7184.92 * 36.42 },
    { fecha: ymd(2), cajas: 209, pedidos: 3, totalUsd: 3253.6, totalVes: 3253.6 * 36.42 },
    { fecha: ymd(1), cajas: 167, pedidos: 3, totalUsd: 5121.74, totalVes: 5121.74 * 36.42 },
    { fecha: ymd(0), cajas: 140, pedidos: 3, totalUsd: 3288.65, totalVes: 3288.65 * 36.42 },
  ],
  demo: true,
};

export const demoIntegrations = [
  {
    name: 'bcv',
    label: 'Tasas BCV (USD / EUR)',
    configured: false,
    status: 'no_configurado',
    detail: 'Modo demostración: tasas fijas de contingencia.',
  },
  {
    name: 'tfhka',
    label: 'The Factory HKA — imprenta digital',
    configured: false,
    status: 'no_configurado',
    detail: 'TFHKA no configurado en el entorno demo.',
  },
];
